/**
 * Como a tela de Administração fala com o servidor.
 *
 * Ela usa o mesmo `api` do painel dos pastores, e por isso herda as três coisas que valem para
 * todo pedido daqui: endereço relativo, sessão em cookie que o JavaScript não lê, e erro do
 * servidor virando a frase que ele escreveu.
 *
 * **O que é diferente é a PORTA.** A Administração responde em `/v1/hospedagem/*`, com cookie
 * próprio (`adsa_hospedagem`) e sessão de doze horas. Nenhuma conta de painel alcança esta porta,
 * e esta conta não alcança nenhuma tela de painel: são dois mundos, e o arquivo separado é o que
 * deixa isso visível para quem abrir o código daqui a um ano.
 *
 * **A entrada é num pedido só**: e-mail, senha e código juntos. O painel pede em dois passos
 * porque é tela de todo dia, no celular; esta é usada pouquíssimas vezes, por uma pessoa só, e um
 * pedido só não deixa estado pela metade guardado em lugar nenhum.
 */
import { api } from './api'

/**
 * A resposta de `/v1/hospedagem/eu`: ou ninguém entrou, ou é alguém e vem tudo.
 *
 * Igual à do painel, e pelo mesmo motivo: é a primeira pergunta que a página faz ao abrir, e
 * recusar com 401 sujaria o console de quem só abriu a tela de entrar.
 */
export type QuemEstaNaAdministracao =
  | { dentro: false }
  | ({ dentro: true } & EuDaAdministracao)

export interface EuDaAdministracao {
  nome: string
  email: string
  /** Enquanto for verdade, a conta não alcança Campo nenhum. Quem decide é o servidor. */
  deve_ligar_segundo_fator: boolean
  /** Quantos dos dez códigos de emergência ainda não foram gastos. Nulo antes de ligar. */
  codigos_de_emergencia_restantes: number | null
}

export interface GemeoNaLista {
  id: string
  nome: string
  /** Sobe de um a cada "Recomeçar". Zero é o treinamento como ele nasceu. */
  geracao: number
  /**
   * Os notebooks de treinamento inscritos nele.
   *
   * `atrasados` são os que ainda estão numa geração anterior: eles existiam antes do último
   * "Recomeçar", e o que está dentro deles é de um treinamento que não existe mais.
   */
  notebooks: { ligados: number; atrasados: number }
}

export interface CampoNaLista {
  id: string
  nome: string
  ativo: boolean
  criado_em: string
  /** O treinamento deste Campo, como SUB-LINHA dele. Nulo é "ainda não tem". */
  gemeo: GemeoNaLista | null
  notebooks_ligados: number
  notebooks_cortados: number
  /** A hora do último evento que um notebook mandou. Nulo é "nenhum mandou ainda". */
  ultimo_envio: string | null
  congregacoes: number
  pessoas: number
  /** Só enquanto nenhum notebook lançou nada. Depois disso o caminho é desativar. */
  pode_apagar: boolean
  /** Quantas pessoas ainda não criaram a senha pelo link. */
  ainda_nao_ativaram: number
  /** Quem é quem, com o e-mail e como foi o envio do link. */
  gente: PessoaNoCartao[]
  /** As congregações, com quais ainda se renomeiam. */
  lista_de_congregacoes: CongregacaoNoCartao[]
}

/**
 * Como foi o último envio do link, pela resposta do serviço de e-mail.
 *
 * `ENVIADO` quer dizer que o serviço ACEITOU o envio: daí em diante é o provedor de quem recebe,
 * e o servidor não sabe se caiu na caixa de spam.
 */
export type EnvioDoLink =
  | { como: 'NUNCA' }
  | { como: 'NA_FILA'; quando: string }
  | { como: 'ENVIADO'; quando: string }
  | { como: 'TENTANDO_DE_NOVO'; quando: string; porque: string }
  | { como: 'FALHOU'; quando: string; porque: string }

export type CargoDaTela = 'pastor_do_campo' | 'tesoureiro_do_campo' | 'pastor_dirigente' | 'tesoureiro'

export interface PessoaNoCartao {
  id: string
  nome: string
  email: string
  cargo: string
  congregacao_id: string
  congregacao: string
  limite_aprovacao: number | null
  /** Já criou a senha: daí em diante, o cadastro dela é da sede, e não se corrige aqui. */
  criou_a_senha: boolean
  tem_conta_de_painel: boolean
  /** A conta do painel dela, e se o segundo fator está ligado. Nula sem conta. */
  conta_de_painel: { id: string; tem_segundo_fator: boolean } | null
  envio: EnvioDoLink
}

export interface CongregacaoNoCartao {
  id: string
  nome: string
  e_sede: boolean
  /** Nenhum notebook é dela e nenhum lançou nada nela. */
  pode_renomear: boolean
}

export interface CongregacaoDoCampo {
  id: string
  nome: string
  e_sede: boolean
}

export interface CampoCriado {
  campo_id: string
  nome: string
  /** Falso quando o pedido já tinha sido feito: o mesmo Campo volta, e nenhum link sai de novo. */
  novo: boolean
  eventos: number
  congregacoes: { id: string; nome: string; e_sede: boolean }[]
  pessoas: { id: string; nome: string; email: string; cargo: string; congregacao_id: string }[]
  links_mandados: number
  /** Quem ficou sem conta de painel, e por quê. Vazio no caso normal. */
  avisos: string[]
}

/** Um e-mail guardado na caixa de saída, em vez de mandado. */
export interface MensagemDaCaixa {
  id: string
  para: string
  assunto: string
  corpo_texto: string
  criado_em: string
}

export interface CodigoDoNotebook {
  codigo: string
  expira_em: string
  validade_em_horas: number
  recado: string
}

export interface TreinamentoCriado {
  gemeo_id: string
  nome: string
  novo: boolean
  geracao: number
  linhas_copiadas: number
  recado: string
}

/** O que a tela manda para criar um Campo. Os nomes são os do servidor, sem tradução no meio. */
export interface PedidoDeCampoNovo {
  pedido_id: string
  nome: string
  congregacoes: { nome: string; e_sede: boolean }[]
  timbre?: string[]
  pessoas: {
    nome: string
    email: string
    cargo: CargoDaTela
    congregacao: string
    /** Em centavos. **Vazio quer dizer SEM LIMITE**, e só o Pastor do Campo nasce assim. */
    limite_aprovacao: number | null
  }[]
  mandar_os_links?: boolean
}

export const administracao = {
  eu: () => api.get<QuemEstaNaAdministracao>('/v1/hospedagem/eu'),

  entrar: (email: string, senha: string, codigo: string) =>
    api.post<{
      dentro: true
      nome: string
      email: string
      deve_ligar_segundo_fator: boolean
      expira_em: string
    }>('/v1/hospedagem/entrar', {
      email,
      senha,
      /* Vazio só na primeiríssima entrada, quando o fator ainda não existe. */
      ...(codigo === '' ? {} : { codigo })
    }),

  sair: () => api.post<{ fora: true }>('/v1/hospedagem/sair'),

  comecarOFator: () =>
    api.post<{ endereco: string; segredo: string; quadrado: string }>(
      '/v1/hospedagem/segundo-fator/comecar'
    ),

  confirmarOFator: (codigo: string) =>
    api.post<{ ligado: true; codigos_de_emergencia: string[] }>(
      '/v1/hospedagem/segundo-fator/confirmar', { codigo }
    ),

  campos: () => api.get<{ campos: CampoNaLista[] }>('/v1/hospedagem/campos'),

  congregacoesDe: (campoId: string) =>
    api.get<{ congregacoes: CongregacaoDoCampo[] }>(
      `/v1/hospedagem/campos/${encodeURIComponent(campoId)}/congregacoes`
    ),

  criarCampo: (pedido: PedidoDeCampoNovo) =>
    api.post<CampoCriado>('/v1/hospedagem/campos', pedido),

  /** Apagar pede o motivo **e a senha**: é a única ação desta tela que desfaz coisa. */
  apagar: (campoId: string, motivo: string, senha: string) =>
    api.post<{
      apagado: true
      nome: string
      eventos: number
      notebooks_cortados: number
      codigos_queimados: number
      contas_do_painel_desativadas: number
    }>(`/v1/hospedagem/campos/${encodeURIComponent(campoId)}/apagar`, { motivo, senha }),

  desativar: (campoId: string, motivo: string) =>
    api.post<{ desativado: true; nome: string; notebooks_cortados: number }>(
      `/v1/hospedagem/campos/${encodeURIComponent(campoId)}/desativar`, { motivo }
    ),

  criarOTreinamento: (campoId: string) =>
    api.post<TreinamentoCriado>(
      `/v1/hospedagem/campos/${encodeURIComponent(campoId)}/treinamento`
    ),

  recomecarOTreinamento: (campoId: string, motivo: string) =>
    api.post<{ recomecado: true; geracao: number; sequencia_de_corte: string; linhas: number }>(
      `/v1/hospedagem/campos/${encodeURIComponent(campoId)}/treinamento/recomecar`, { motivo }
    ),

  codigoDoNotebook: (
    campoId: string,
    pedido: { congregacao_id: string; nome_do_notebook?: string; validade_em_horas?: number }
  ) =>
    api.post<CodigoDoNotebook>(
      `/v1/hospedagem/campos/${encodeURIComponent(campoId)}/codigo`, pedido
    ),

  caixaDeSaida: () =>
    api.get<{ ativa: boolean; mensagens: MensagemDaCaixa[] }>('/v1/hospedagem/caixa-de-saida'),

  mandarOsLinksDeNovo: (campoId: string) =>
    api.post<{ mandados: number }>(
      `/v1/hospedagem/campos/${encodeURIComponent(campoId)}/primeiro-acesso`
    ),

  /* ------------------------------------------------- corrigir o cadastro de quem não ativou */

  corrigirAPessoa: (
    campoId: string,
    pessoaId: string,
    pessoa: {
      nome: string
      email: string
      cargo: CargoDaTela
      congregacao_id: string
      limite_aprovacao: number | null
    }
  ) =>
    api.post<{ mudou: string[]; link_mandado: boolean; avisos: string[] }>(
      `/v1/hospedagem/campos/${encodeURIComponent(campoId)}/pessoas/${encodeURIComponent(pessoaId)}`,
      pessoa
    ),

  tirarAPessoa: (campoId: string, pessoaId: string) =>
    api.post<{ tirada: true; nome: string }>(
      `/v1/hospedagem/campos/${encodeURIComponent(campoId)}/pessoas/${encodeURIComponent(pessoaId)}/tirar`
    ),

  /** Desligar o segundo fator de uma conta do painel: o motivo e o código desta conta. */
  desligarOSegundoFatorDe: (contaId: string, motivo: string, codigo: string) =>
    api.post<{ desligado: true; nome: string; havia_segundo_fator: boolean; aviso_por_email: boolean }>(
      `/v1/hospedagem/contas-do-painel/${encodeURIComponent(contaId)}/desligar-segundo-fator`,
      { motivo, codigo }
    ),
  mandarOLinkDe: (campoId: string, pessoaId: string) =>
    api.post<{ mandado: true }>(
      `/v1/hospedagem/campos/${encodeURIComponent(campoId)}/pessoas/${encodeURIComponent(pessoaId)}/link`
    ),

  acrescentarACongregacao: (
    campoId: string,
    pedido: {
      pedido_id: string
      nome: string
      dirigente?: { nome: string; email: string; limite_aprovacao: number }
    }
  ) =>
    api.post<{
      congregacao_id: string
      novo: boolean
      pessoa_id: string | null
      link_mandado: boolean
      avisos: string[]
    }>(`/v1/hospedagem/campos/${encodeURIComponent(campoId)}/congregacoes`, pedido),

  renomearACongregacao: (campoId: string, congregacaoId: string, nome: string) =>
    api.post<{ mudou: boolean }>(
      `/v1/hospedagem/campos/${encodeURIComponent(campoId)}/congregacoes/${encodeURIComponent(congregacaoId)}/nome`,
      { nome }
    )
}

/**
 * Os quatro cargos que a tela cria, com o nome que a pessoa lê e a regra do limite.
 *
 * Os nomes são os do notebook (`NOME_DO_CARGO`, em `src/compartilhado/cargos.ts`), e a ordem é a
 * de lá: a equipe já conhece estas palavras.
 */
export const CARGOS: {
  valor: CargoDaTela
  titulo: string
  /** Nasce SEM limite de propósito: só o Pastor do Campo. */
  semLimite: boolean
  /** Nasce com limite ZERO, e a tela nem pergunta: o Tesoureiro da congregação não aprova. */
  limiteZero: boolean
  explicacao: string
}[] = [
  {
    valor: 'pastor_do_campo',
    titulo: 'Pastor do Campo',
    /** É o único que nasce SEM limite de propósito, e o único administrador máximo. */
    semLimite: true,
    limiteZero: false,
    explicacao: 'administra o cadastro, aprova sem limite e enxerga todas as congregações'
  },
  {
    valor: 'pastor_dirigente',
    titulo: 'Pastor Dirigente',
    semLimite: false,
    limiteZero: false,
    explicacao: 'aprova e reabre o mês da congregação dele, pelo celular, e não vê as outras'
  },
  {
    valor: 'tesoureiro_do_campo',
    titulo: 'Tesoureiro do Campo',
    semLimite: false,
    limiteZero: false,
    explicacao: 'lança e confere em todas as congregações, e não aprova nada'
  },
  {
    valor: 'tesoureiro',
    titulo: 'Tesoureiro',
    semLimite: false,
    limiteZero: true,
    explicacao: 'lança na congregação dele, e não vê as outras. Não aprova nada: o limite nasce em zero'
  }
]

/** O nome de tela de um cargo guardado. Cargo que esta tela não conhece aparece como veio. */
export function nomeDoCargo(cargo: string): string {
  return CARGOS.find((c) => c.valor === cargo)?.titulo ?? cargo
}
